import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "react-query";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/logowhite.png";
import useSetup from "../../hooks/useSetup";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import { getBillingOffers, fetchData } from "../../api/client";
import { useAuthContext } from "../../context/AuthContext";
import { useOnboarding } from "../../context/OnboardingContext";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";

// Componentes separados por etapa
import EtapaEmpresa from "../../components/EtapaEmpresa";
import EtapaClientesNiveis from "../../components/EtapaClientesNiveis";
import EtapaClientesCadastro from "../../components/EtapaClientesCadastro";
// import EtapaClientesClassificacao from "../../components/EtapaClientesClassificacao";
import EtapaProdutos from "../../components/EtapaProdutos";
import EtapaPipeline from "../../components/EtapaPipeline";
import EtapaSolicitacoes from "../../components/EtapaSolicitacoes";
import EtapaCanais from "../../components/EtapaCanais";
import EtapaCargos from "../../components/EtapaCargos";
import EtapaUsuarios from "../../components/EtapaUsuarios";
import EtapaResumoFinal from "../../components/EtapaResumoFinal";
import { findCardById } from "../../constants/channelCatalog";
import { resolveSnapshotProjectId } from "../../utils/primaryProjectId";
import { awarenessSetupLevelsFromGroups } from "../../utils/awarenessGroupTree";

const SETUP_STEP_KEYS = [
  "company",
  "clientsLevels",
  "clientsImport",
  "products",
  "pipeline",
  "requests",
  "channels",
  "roles",
  "users",
  "summary",
];

const SetupPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyKey } = useWorkspaceConfig();
  const { userData } = useAuthContext();
  const userId = userData?.id != null ? Number(userData.id) : null;
  const { markStepCompleted } = useOnboarding();
  const { t, i18n } = useTranslation("onboarding");

  /** Incrementa após o bundle lazy-load; `t` não muda de referência quando o JSON entra na store. */
  const [onboardingNsTick, setOnboardingNsTick] = useState(() => {
    const lng = i18n.resolvedLanguage || i18n.language || "pt-BR";
    return i18n.hasResourceBundle(lng, "onboarding") ? 1 : 0;
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      await ensureNamespaceLoaded("onboarding");
      if (!alive) return;
      // Sempre re-renderiza memos que usam `t`: um filho pode carregar o namespace antes deste efeito;
      // nesse caso hadBundle já seria true e os rótulos do stepper ficariam presos nas chaves.
      setOnboardingNsTick((n) => n + 1);
    })();
    return () => {
      alive = false;
    };
  }, [i18n, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    let mounted = true;
    getBillingOffers({ autoGenerate: 1 })
      .then((res) => {
        if (!mounted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        if (items.length > 0) {
          setBillingHint("Detectamos oportunidade de upgrade/add-on para este workspace.");
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const steps = useMemo(
    () => SETUP_STEP_KEYS.map((key) => t(`setup.steps.${key}`)),
    [t, i18n.resolvedLanguage, i18n.language, onboardingNsTick]
  );
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billingHint, setBillingHint] = useState("");
  const [savedProjectId, setSavedProjectId] = useState(null);
  const [segmentKey, setSegmentKey] = useState("generic");
  const [segmentTemplates, setSegmentTemplates] = useState(null);
  const [segmentPresets, setSegmentPresets] = useState([]);
  const [setupChannelsList, setSetupChannelsList] = useState([]);
  /** Evita re-hidratar em loop; inclui companyKey para trocar workspace sem F5. */
  const lastSnapshotHydrationKeyRef = useRef("");
  const snapshotHydrationSeqRef = useRef(0);
  const [snapshotReloadBusy, setSnapshotReloadBusy] = useState(false);
  const [snapshotReloadError, setSnapshotReloadError] = useState("");
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [finishError, setFinishError] = useState("");

  // EtapaEmpresa
  const [linkDigitado, setLinkDigitado] = useState("");
  const [company, setCompany] = useState({});

  // EtapaClientes - Níveis de Consciência
  const {
    saveStepData,
    commitWizard,
    generateAwarenessLevels,
    generatePipelineStages,
    generateRequestTypes,
    generateRoles,
    isLoading,
  } = useSetup();

  useEffect(() => {
    let cancelled = false;
    const lng = encodeURIComponent(i18n.resolvedLanguage || i18n.language || "pt-BR");
    fetchData(`/setup/segment-templates?locale=${lng}`, "GET")
      .then((res) => {
        const raw = res?.data ?? res;
        const tpl = raw?.templates || raw;
        const presets = Array.isArray(raw?.presets) ? raw.presets : [];
        if (!cancelled && tpl && typeof tpl === "object") setSegmentTemplates(tpl);
        if (!cancelled && presets.length) setSegmentPresets(presets);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage, i18n.language]);

  const handleSetupChannelsChange = useCallback((list) => {
    setSetupChannelsList(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    if (!segmentPresets.length) return;
    const keys = new Set(segmentPresets.map((p) => p.key));
    try {
      const raw = process.env.REACT_APP_SETUP_DEFAULT_SEGMENT;
      if (raw && keys.has(String(raw).trim())) {
        setSegmentKey(String(raw).trim());
        return;
      }
    } catch {
      /* ignore */
    }
    const h = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
    if (h.includes("varejo") || h.includes("loja")) {
      if (keys.has("retail")) setSegmentKey("retail");
    } else if (h.includes("industr") || h.includes("fabrica")) {
      if (keys.has("industry")) setSegmentKey("industry");
    } else if (h.includes("clinic") || h.includes("saude") || h.includes("med")) {
      if (keys.has("health")) setSegmentKey("health");
    }
  }, [segmentPresets]);

  const [hasImportedAwarenessLevels, setHasImportedAwarenessLevels] = useState(false);
  const [awarenessLevels, setAwarenessLevels] = useState(
    []
    // [
    //   { title: "Desconhece o Problema", description: "Ainda não sabe que tem um problema." },
    //   { title: "Consciente do Problema", description: "Sabe que tem um problema, mas não conhece soluções." },
    //   { title: "Consciente da Solução", description: "Conhece as soluções, mas não seu produto." },
    //   { title: "Consciente do Produto", description: "Conhece seu produto, mas não tem certeza se quer comprar." },
    //   { title: "Mais Consciente", description: "Está pronto para comprar seu produto." }
    // ]
  );
  const [selectedLevels, setSelectedLevels] = useState([true, true, true, false, false]);

  // EtapaClientes - Cadastro/Importação
  const [manualClients, setManualClients] = useState([]);
  const [newClient, setNewClient] = useState({ nome: "", email: "", telefone: "" });
  const [showManualForm, setShowManualForm] = useState(false);
  const [hasImportedClients, setHasImportedClients] = useState(false);

  // EtapaClientes - Classificação: dados placeholder para uso futuro (ex.: classificação por nível)
  // classifiedClients, awarenessColors, awarenessColorsDark podem ser usados em EtapaClientesClassificacao

  // EtapaProdutos
  const [produtos, setProdutos] = useState([]);
  const [showManualProductForm, setShowManualProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ nome: "", categoria: "", preco: "" });
  const [classifiedProducts] = useState([]);

  // EtapaPipeline
  const [pipelineStages, setPipelineStages] = useState([]); // Inicializado como array vazio
  const [newStage, setNewStage] = useState({ nome: "", ordem: 1 });

  // EtapaSolicitacoes
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypesError, setRequestTypesError] = useState("");

  // EtapaPesquisas
  const [pesquisasConfiguradas, setPesquisasConfiguradas] = useState([]);
  
  // EtapaCargos
  const [cargosConfigurados, setCargosConfigurados] = useState([]);
  const [cargosError, setCargosError] = useState("");

  // EtapaUsuarios
  const [manualUsers, setManualUsers] = useState([]);
  const [newUser, setNewUser] = useState({ nome: "", email: "", telefone: "" });
  const [showManualFormUser, setShowManualFormUser] = useState(false);
  const [hasImportedUsers, setHasImportedUsers] = useState(false);

  const applySetupSnapshot = useCallback((snap, options = {}) => {
    const reload = Boolean(options.reload);
    if (!snap || typeof snap !== "object") return;

    const ai = snap.aiContext && typeof snap.aiContext === "object" ? snap.aiContext : {};
    const rawCompany = snap.company && typeof snap.company === "object" ? snap.company : {};
    const c = { ...rawCompany };
    const aiSk = String(ai.segmentKey || "").trim();
    if (!String(c.segmentKey || "").trim() && aiSk) c.segmentKey = aiSk;
    setCompany((prev) => ({ ...prev, ...c }));
    if (c.companyWebsite) setLinkDigitado(String(c.companyWebsite));
    const sk = String(c.segmentKey || "").trim();
    if (sk) {
      if (segmentTemplates && segmentTemplates[sk]) setSegmentKey(sk);
      else if (Array.isArray(segmentPresets) && segmentPresets.some((p) => p.key === sk)) {
        setSegmentKey(sk);
      }
    }

    if (Array.isArray(snap.awarenessLevels) && snap.awarenessLevels.length) {
      setAwarenessLevels(snap.awarenessLevels);
      setSelectedLevels(snap.awarenessLevels.map(() => true));
      setHasImportedAwarenessLevels(true);
    }
    if (Array.isArray(snap.produtos) && snap.produtos.length) {
      setProdutos(snap.produtos);
    }
    if (Array.isArray(snap.requestTypes) && snap.requestTypes.length) {
      setRequestTypes(
        snap.requestTypes.map((rt) => ({
          tipo: rt.tipo || "",
          subtipos:
            Array.isArray(rt.subtipos) && rt.subtipos.length
              ? rt.subtipos.map((s) => String(s))
              : [""],
        }))
      );
    }
    if (Array.isArray(snap.cargos) && snap.cargos.length) {
      setCargosConfigurados(
        snap.cargos.map((cg) => ({
          nome: cg.nome || "",
          descricao: cg.descricao || "",
          ativo: cg.ativo !== false,
        }))
      );
    }
    if (Array.isArray(snap.channels) && snap.channels.length) {
      setSetupChannelsList(snap.channels);
    }
    if (Array.isArray(snap.pipelineStages) && (snap.pipelineStages.length > 0 || reload)) {
      setPipelineStages(snap.pipelineStages);
    }
    if (Array.isArray(snap.manualClients) && (snap.manualClients.length > 0 || reload)) {
      setManualClients(snap.manualClients);
      setHasImportedClients(snap.manualClients.length > 0);
    }
    if (Array.isArray(snap.workspaceUsers) && (snap.workspaceUsers.length > 0 || reload)) {
      setManualUsers(
        snap.workspaceUsers.map((u) => ({
          nome: u.nome || "",
          email: u.email || "",
          telefone: u.telefone || "",
        }))
      );
      setHasImportedUsers(snap.workspaceUsers.length > 0);
    }
  }, [segmentTemplates, segmentPresets]);

  useEffect(() => {
    const sk = String(company.segmentKey || "").trim();
    if (!sk) return;
    if (segmentTemplates?.[sk]) {
      setSegmentKey(sk);
      return;
    }
    if (Array.isArray(segmentPresets) && segmentPresets.some((p) => p.key === sk)) setSegmentKey(sk);
  }, [company.segmentKey, segmentTemplates, segmentPresets]);

  useEffect(() => {
    const pid = resolveSnapshotProjectId(savedProjectId);
    if (!pid) return;
    const hydrationKey = `${pid}|${String(companyKey ?? "")}`;
    if (lastSnapshotHydrationKeyRef.current === hydrationKey) return;

    const seq = ++snapshotHydrationSeqRef.current;
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchData(
          `/setup/snapshot?idProjeto=${encodeURIComponent(pid)}`,
          "GET"
        );
        if (cancelled || seq !== snapshotHydrationSeqRef.current) return;
        const snap = raw?.data ?? raw;
        if (snap && typeof snap === "object") {
          applySetupSnapshot(snap, { reload: false });
        }
        // Mesma fonte que /awareness-stages: GET /clients-groups (ids_projetos na URL evita o
        // interceptor sobrescrever com todos os selecionados e força escopo só deste projeto no backend)
        const cg = await fetchData(
          `/clients-groups?page=1&limit=500&search=&ids_projetos=${encodeURIComponent(pid)}`
        );
        if (cancelled || seq !== snapshotHydrationSeqRef.current) return;
        if (resolveSnapshotProjectId(savedProjectId) !== pid) return;

        const groups = Array.isArray(cg?.data) ? cg.data : [];
        const fromStages = awarenessSetupLevelsFromGroups(groups, pid);
        if (fromStages.length) {
          setAwarenessLevels(fromStages);
          setSelectedLevels(fromStages.map(() => true));
          setHasImportedAwarenessLevels(true);
        }
        lastSnapshotHydrationKeyRef.current = hydrationKey;
      } catch (e) {
        console.warn("[SetupPage] snapshot / awareness", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [savedProjectId, applySetupSnapshot, companyKey]);

  //EtapaAutomacoes
  const suggestedRules = [
    {
      evento: "Novo Cliente Adicionado",
      condicao: "O cliente preencheu o formulário de cadastro",
      acao: "Enviar e-mail de boas-vindas",
    },
    {
      evento: "Compra Realizada",
      condicao: "O valor da compra é superior a R$ 500",
      acao: "Enviar cupom de desconto para próxima compra",
    },
    {
      evento: "Carrinho Abandonado",
      condicao: "O cliente não completou a compra dentro de 24 horas",
      acao: "Enviar lembrete por e-mail",
    },
  ];
  
  const eventosDisponiveis = [
    "Novo Cliente Adicionado",
    "Compra Realizada",
    "Carrinho Abandonado",
    "Visita ao Site",
    "Página de Produto Visualizada",
  ];
  
  const condicoesDisponiveis = [
    "O cliente preencheu o formulário de cadastro",
    "O valor da compra é superior a R$ 500",
    "O cliente não completou a compra dentro de 24 horas",
    "O cliente visitou a página de produto",
  ];
  
  const acoesDisponiveis = [
    "Enviar e-mail de boas-vindas",
    "Enviar cupom de desconto para próxima compra",
    "Enviar lembrete por e-mail",
    "Enviar e-mail com promoção do produto",
  ];  

  const [selectedSuggestedRules, setSelectedSuggestedRules] = useState([0, 2]);
  const [manualRules, setManualRules] = useState([
    {
      evento: "Visita ao Site",
      condicao: "O cliente visitou a página de produto",
      acao: "Enviar e-mail com promoção do produto",
    },
  ]);
  const [manualRule, setManualRule] = useState({
    evento: "",
    condicao: "",
    acao: "",
  });

  const applySegmentTemplate = useCallback(
    (key) => {
      const tpl =
        segmentTemplates?.[key] || segmentTemplates?.generic || segmentTemplates?.other;
      if (!tpl) {
        setSegmentKey(key);
        return;
      }
      setSegmentKey(key);
      const presetLabel = segmentPresets.find((p) => p.key === key)?.label;
      setCompany((prev) => ({
        ...prev,
        segmento:
          prev.segmento ||
          presetLabel ||
          t(`setup.segmentLabels.${key}`, { defaultValue: key }),
      }));
      if (Array.isArray(tpl.awarenessLevels) && tpl.awarenessLevels.length) {
        setAwarenessLevels(tpl.awarenessLevels);
        setSelectedLevels(tpl.awarenessLevels.map(() => true));
        setHasImportedAwarenessLevels(true);
      }
      if (tpl.requestSubtypes && typeof tpl.requestSubtypes === "object") {
        const a = tpl.requestSubtypes.Atendimento;
        setRequestTypes([
          { tipo: "Atendimento", subtipos: Array.isArray(a) && a.length ? [...a] : [""] },
        ]);
      }
      if (Array.isArray(tpl.roles) && tpl.roles.length) {
        setCargosConfigurados(
          tpl.roles.map((r, idx) => ({
            nome: r.nome || `Cargo ${idx + 1}`,
            descricao: r.descricao || "",
            ativo: true,
          }))
        );
      }
      if (Array.isArray(tpl.pipelineLabels) && tpl.pipelineLabels.length) {
        const data = tpl.pipelineLabels;
        const formattedData = data.map((step, index) => ({
          from: step.title,
          to: index < data.length - 1 ? data[index + 1].title : "",
          fromDescription: step.description,
          toDescription: index < data.length - 1 ? data[index + 1].description : "",
        }));
        formattedData.pop();
        setPipelineStages(formattedData);
      } else if (
        tpl.requestSubtypes &&
        typeof tpl.requestSubtypes === "object" &&
        Array.isArray(tpl.requestSubtypes.Venda) &&
        tpl.requestSubtypes.Venda.length
      ) {
        const v = tpl.requestSubtypes.Venda.map((x) => String(x || "").trim()).filter(Boolean);
        if (v.length >= 2) {
          const formattedData = v.map((title, index) => ({
            from: title,
            to: index < v.length - 1 ? v[index + 1] : "",
            fromDescription: "",
            toDescription: "",
          }));
          formattedData.pop();
          setPipelineStages(formattedData);
        } else if (v.length === 1) {
          setPipelineStages([
            { from: v[0], to: "", fromDescription: "", toDescription: "" },
          ]);
        }
      }
    },
    [segmentTemplates, segmentPresets, t, i18n.resolvedLanguage, i18n.language, onboardingNsTick]
  );

  const segmentPresetOptions = useMemo(() => {
    if (segmentPresets.length > 0) {
      return segmentPresets.map((p) => ({ key: p.key, label: p.label || p.key }));
    }
    const keys = segmentTemplates ? Object.keys(segmentTemplates) : [];
    return keys.map((key) => ({
      key,
      label: t(`setup.segmentLabels.${key}`, { defaultValue: key }),
    }));
  }, [segmentPresets, segmentTemplates, t, i18n.resolvedLanguage, i18n.language, onboardingNsTick]);

  const phaseHint = useMemo(() => {
    if (activeStep <= 2) return t("setup.phases.business");
    if (activeStep <= 5) return t("setup.phases.sales");
    if (activeStep <= 8) return t("setup.phases.team");
    return t("setup.phases.review");
  }, [activeStep, t, i18n.resolvedLanguage, i18n.language, onboardingNsTick]);

  const companyForAi = useMemo(
    () => ({
      ...company,
      segmentKey,
      segmento: company.segmento || segmentKey,
    }),
    [company, segmentKey]
  );

  const resolveProjectId = () => resolveSnapshotProjectId(savedProjectId);

  const handleReloadSnapshot = async () => {
    setSnapshotReloadError("");
    const pid = resolveProjectId();
    if (!pid) return;
    setSnapshotReloadBusy(true);
    lastSnapshotHydrationKeyRef.current = "";
    snapshotHydrationSeqRef.current += 1;
    try {
      const raw = await fetchData(
        `/setup/snapshot?idProjeto=${encodeURIComponent(pid)}`,
        "GET"
      );
      const snap = raw?.data ?? raw;
      if (!snap || typeof snap !== "object") {
        throw new Error("empty snapshot");
      }
      applySetupSnapshot(snap, { reload: true });
      const cg = await fetchData(
        `/clients-groups?page=1&limit=500&search=&ids_projetos=${encodeURIComponent(pid)}`
      );
      const groups = Array.isArray(cg?.data) ? cg.data : [];
      const fromStages = awarenessSetupLevelsFromGroups(groups, pid);
      if (fromStages.length) {
        setAwarenessLevels(fromStages);
        setSelectedLevels(fromStages.map(() => true));
        setHasImportedAwarenessLevels(true);
      }
      lastSnapshotHydrationKeyRef.current = `${pid}|${String(companyKey ?? "")}`;
    } catch (e) {
      console.warn("[SetupPage] reload snapshot", e);
      setSnapshotReloadError(t("setup.reloadFromServerError"));
    } finally {
      setSnapshotReloadBusy(false);
    }
  };

  const handleToggleLevel = (index) => {
    const newSelected = [...selectedLevels];
    newSelected[index] = !newSelected[index];
    setSelectedLevels(newSelected);
  };

  const toggleAll = () => {
    const allSelected = selectedLevels.every(Boolean);
    setSelectedLevels(awarenessLevels.map(() => !allSelected));
  };

  const handleAddManualClient = () => {
    if (newClient.nome && newClient.email) {
      setManualClients((prev) => [...prev, newClient]);
      setNewClient({ nome: "", email: "", telefone: "" });
      setHasImportedClients(true);
    }
  };

  const handleAddManualUser = () => {
    if (newUser.nome && newUser.email) {
      setManualUsers((prev) => [...prev, newUser]);
      setNewUser({ nome: "", email: "", telefone: "" });
      setHasImportedUsers(true);
    }
  };

  const handleNext = async () => {
    setFinishError("");
    setLoading(true);
    try {
      if (activeStep === 0 && company && company.companyName) {
        const uid = userId != null ? Number(userId) : 0;
        if (!uid) {
          setFinishError(t("setup.errors.userIdRequired"));
          return;
        }
        try {
            const out = await saveStepData(1, {
              userId: uid,
              companyName: company.companyName,
              companyAbout: company.companyAbout,
              companyWebsite: company.companyWebsite,
              segmento: company.segmento,
              publicoAlvo: company.publicoAlvo,
              focoPrincipal: company.focoPrincipal,
              maturidadeDigital: company.maturidadeDigital,
              capacidadeEstrategica: company.capacidadeEstrategica,
            });
            const raw = out?.data ?? out;
            const pid = raw?.id ?? out?.id;
            if (pid != null && Number.isFinite(Number(pid))) {
              const n = Number(pid);
              setSavedProjectId(n);
              try {
                sessionStorage.setItem("selectedCompanies", JSON.stringify([n]));
                localStorage.setItem("activeProjectId", String(n));
                sessionStorage.setItem("activeCompany", String(n));
              } catch {
                /* ignore */
              }
            }
        } catch (e) {
          console.error("Falha ao salvar empresa durante o setup guiado:", e);
          setFinishError(e?.message || t("setup.errors.saveCompanyFailed"));
          return;
        }
      }

      if (activeStep === 4) {
        const idProjeto = resolveProjectId();
        if (idProjeto) {
          try {
            await saveStepData(13, { idProjeto, pipelineStages });
          } catch (e) {
            console.warn("[SetupPage] pipeline draft", e);
          }
        }
      }

      if (activeStep === steps.length - 1) {
        const idProjeto = resolveProjectId();
        if (!idProjeto) {
          setFinishError(t("setup.finishNeedProject"));
          return;
        }
        try {
          await commitWizard({
            idProjeto,
            segmentKey,
            awarenessLevels,
            selectedLevels,
            manualClients,
            manualUsers,
            pipelineStages,
            produtos,
            requestTypes,
            cargos: cargosConfigurados,
            company: {
              companyName: company.companyName,
              companyAbout: company.companyAbout,
              companyWebsite: company.companyWebsite,
              segmento: company.segmento,
              publicoAlvo: company.publicoAlvo,
              focoPrincipal: company.focoPrincipal,
              maturidadeDigital: company.maturidadeDigital,
              capacidadeEstrategica: company.capacidadeEstrategica,
            },
            channelsSetupSummary: {
              count: setupChannelsList.length,
              tipoIds: [
                ...new Set(
                  setupChannelsList
                    .map((ch) => Number(ch.tipo))
                    .filter((n) => Number.isFinite(n))
                ),
              ],
            },
          });
          try {
            const projectKey =
              sessionStorage.getItem("activeCompany") ||
              localStorage.getItem("activeProjectId") ||
              (() => {
                const c = localStorage.getItem("company");
                return c && /^\d+$/.test(String(c).trim()) ? c : "";
              })();
            if (projectKey) {
              localStorage.setItem(`workspace-setup-done-${projectKey}`, "1");
            }
          } catch {
            /* ignore */
          }
          markStepCompleted("core-onboarding-done");
          markStepCompleted("step-guided-setup");
          await queryClient.invalidateQueries({ queryKey: ["setup-onboarding-status"] });
          navigate("/chat");
        } catch (e) {
          const msg =
            (typeof e?.message === "string" && e.message) ||
            t("setup.errors.commitFailed");
          setFinishError(msg);
        }
        return;
      }

      setActiveStep((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestExit = () => {
    setFinishError("");
    setExitDialogOpen(true);
  };

  const confirmExitToApp = () => {
    setExitDialogOpen(false);
    navigate("/chat");
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <EtapaEmpresa
            company={company}
            setCompany={setCompany}
            linkDigitado={linkDigitado}
            setLinkDigitado={setLinkDigitado}
            segmentKey={segmentKey}
            segmentPresetOptions={segmentPresetOptions}
            onSegmentPresetChange={applySegmentTemplate}
          />
        );
      case 1:
        // console.log("renderStepContent switch")
          return (
              <EtapaClientesNiveis
              hasImportedAwarenessLevels={hasImportedAwarenessLevels}
              setHasImportedAwarenessLevels={setHasImportedAwarenessLevels}
              generateAwarenessLevels={generateAwarenessLevels}
              isLoading={isLoading}
              company={companyForAi}
              awarenessLevels={awarenessLevels}
              setAwarenessLevels={setAwarenessLevels}
              selectedLevels={selectedLevels}
              handleToggleLevel={handleToggleLevel}
              toggleAll={toggleAll}
              allSelected={selectedLevels.every(Boolean)}
              />
          );
      case 2:
            return (
                <EtapaClientesCadastro
                showManualForm={showManualForm}
                setShowManualForm={setShowManualForm}
                manualClients={manualClients}
                newClient={newClient}
                setNewClient={setNewClient}
                handleAddManualClient={handleAddManualClient}
                hasImportedClients={hasImportedClients}
                />
            );
      // case 3:
      //       return (
      //           <EtapaClientesClassificacao
      //           classifiedClients={classifiedClients}
      //           awarenessColors={awarenessColors}
      //           awarenessColorsDark={awarenessColorsDark}
      //           />
      //       );
      case 3:
        return (
          <EtapaProdutos
            showManualProductForm={showManualProductForm}
            setShowManualProductForm={setShowManualProductForm}
            manualProducts={produtos}
            setManualProducts={setProdutos}
            newProduct={newProduct}
            setNewProduct={setNewProduct}
            handleAddManualProduct={() => {
              if (newProduct.nome && newProduct.preco) {
                setProdutos((prev) => [...prev, newProduct]);
                setNewProduct({ nome: "", categoria: "", preco: "" });
              }
            }}
            classifiedProducts={classifiedProducts}
          />
        );
      case 4:
        return (
          <EtapaPipeline
            company={companyForAi}
            generatePipelineStages={generatePipelineStages}
            pipelineSteps={pipelineStages}
            setPipelineStages={setPipelineStages}
            newStage={newStage}
            setNewStage={setNewStage}
            handlePipelineChange={(index, field, value) => {
              const updatedSteps = [...pipelineStages];
              updatedSteps[index][field] = value;
              setPipelineStages(updatedSteps);
            }}
            handleAddStep={() => {
              const newStep = { from: "", to: "" };
              setPipelineStages((prevSteps) => [...prevSteps, newStep]);
            }}
            handleRemoveStep={(index) => {
              const updatedSteps = [...pipelineStages];
              updatedSteps.splice(index, 1);
              setPipelineStages(updatedSteps);
            }}
          />
        );
      case 5:
        return (
          <EtapaSolicitacoes
            requestTypes={requestTypes}
            isGenerating={isLoading}
            errorMessage={requestTypesError}
            onGenerateRequestTypes={async () => {
              setRequestTypesError("");
              try {
                const suggestedRaw = await generateRequestTypes(companyForAi);
                const suggested = Array.isArray(suggestedRaw)
                  ? suggestedRaw
                  : Array.isArray(suggestedRaw?.data)
                  ? suggestedRaw.data
                  : [];

                if (Array.isArray(suggested) && suggested.length) {
                  const atendimento = new Set();

                  suggested.forEach((item) => {
                    const raiz = String(item.nome || item.tipo || item.type || "")
                      .toLowerCase();
                    const isVenda =
                      raiz.includes("venda") ||
                      raiz.includes("comercial") ||
                      raiz.includes("oportunidade");
                    if (isVenda) return;

                    const level1 = Array.isArray(item.subtipos) ? item.subtipos : [];
                    level1.forEach((s1) => {
                      if (s1?.nome) atendimento.add(String(s1.nome).trim());
                      if (Array.isArray(s1.subtipos)) {
                        s1.subtipos.forEach((s2) => {
                          if (s2?.nome) atendimento.add(String(s2.nome).trim());
                          if (Array.isArray(s2.subtipos)) {
                            s2.subtipos.forEach((s3) => {
                              if (s3?.nome) atendimento.add(String(s3.nome).trim());
                            });
                          }
                        });
                      }
                    });
                  });

                  setRequestTypes([
                    {
                      tipo: "Atendimento",
                      subtipos:
                        atendimento.size > 0 ? Array.from(atendimento) : [""],
                    },
                  ]);
                } else {
                  setRequestTypesError(
                    "Não encontramos sugestões automáticas neste momento. Você pode cadastrar os tipos manualmente."
                  );
                }
              } catch (e) {
                setRequestTypesError(
                  "Não foi possível gerar sugestões com IA agora. Verifique sua conexão ou tente novamente mais tarde. Você pode seguir cadastrando os tipos manualmente."
                );
              }
            }}
            handleTipoChange={(index, value) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[index].tipo = value;
              setRequestTypes(newRequestTypes);
            }}
            handleSubtipoChange={(tipoIndex, subtipoIndex, value) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos[subtipoIndex] = value;
              setRequestTypes(newRequestTypes);
            }}
            handleAddTipo={() => {
              setRequestTypes([...requestTypes, { tipo: "", subtipos: [""] }]);
            }}
            handleRemoveTipo={(index) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes.splice(index, 1);
              setRequestTypes(newRequestTypes);
            }}
            handleAddSubtipo={(tipoIndex) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos.push("");
              setRequestTypes(newRequestTypes);
            }}
            handleRemoveSubtipo={(tipoIndex, subtipoIndex) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos.splice(subtipoIndex, 1);
              setRequestTypes(newRequestTypes);
            }}
          />
        );
      case 6:
        return <EtapaCanais onChannelsChange={handleSetupChannelsChange} />;
      case 7:
        return (
          <EtapaCargos
            roles={cargosConfigurados}
            onRolesChange={setCargosConfigurados}
            isGenerating={isLoading}
            errorMessage={cargosError}
            onGenerateRoles={async () => {
              setCargosError("");
              try {
                const suggestedRaw = await generateRoles(companyForAi);
                const suggested = Array.isArray(suggestedRaw)
                  ? suggestedRaw
                  : Array.isArray(suggestedRaw?.data)
                  ? suggestedRaw.data
                  : [];

                if (Array.isArray(suggested) && suggested.length) {
                  const normalized = suggested.map((item, idx) => ({
                    nome: item.cargo || item.nome || `Cargo ${idx + 1}`,
                    descricao: item.descricao || (typeof item === "string" ? item : ""),
                    ativo: true,
                  }));

                  setCargosConfigurados(normalized);
                } else {
                  setCargosError(
                    "Não encontramos sugestões automáticas de cargos neste momento. Você pode cadastrar os cargos manualmente."
                  );
                }
              } catch (e) {
                setCargosError(
                  "Não foi possível gerar sugestões de cargos com IA agora. Verifique sua conexão ou tente novamente mais tarde. Você pode seguir cadastrando os cargos manualmente."
                );
              }
            }}
          />
        );
      case 8:
        return (
          <EtapaUsuarios 
            showManualForm={showManualFormUser}
            setShowManualForm={setShowManualFormUser}
            manualUsers={manualUsers}
            newUser={newUser}
            setNewUser={setNewUser}
            handleAddManualUser={handleAddManualUser}
            hasImportedUsers={hasImportedUsers}
          />
        );
      case 9:
        return (
          <EtapaResumoFinal
            companyName={
              company.companyName ||
              company.nome ||
              company.name ||
              ""
            }
            selectedAwarenessTitles={
              Array.isArray(awarenessLevels) && awarenessLevels.length
                ? awarenessLevels
                    .map((level, idx) =>
                      selectedLevels[idx] && level?.title
                        ? level.title
                        : null
                    )
                    .filter(Boolean)
                : []
            }
            manualClients={manualClients}
            manualProducts={produtos}
            pipelineSteps={pipelineStages}
            requestTypes={requestTypes}
            channelsLines={
              setupChannelsList.length
                ? setupChannelsList.map((ch) => {
                    const card = findCardById(Number(ch.tipo));
                    const nm = ch.nome || ch.name || "—";
                    return `${nm} (${card?.label || ch.tipo})`;
                  })
                : []
            }
            pesquisasConfiguradas={pesquisasConfiguradas}
            cargosConfigurados={cargosConfigurados}
            usuariosCadastrados={manualUsers}
          />
        );
      default:
        return null;
    }
  };

  // Marca que o workspace já visitou o setup (mesmo que não tenha concluído)
  useEffect(() => {
    try {
      const projectKey =
        sessionStorage.getItem("activeCompany") ||
        localStorage.getItem("activeProjectId") ||
        (() => {
          const c = localStorage.getItem("company");
          return c && /^\d+$/.test(String(c).trim()) ? c : "";
        })();
      if (projectKey) {
        localStorage.setItem(`workspace-setup-visited-${projectKey}`, "1");
      }
    } catch {
      // falha silenciosa
    }
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
    //   justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #121212, #1e1e1e)"
          : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        padding: 2,
        overflow: "auto"
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
        <img
          src={theme.palette.mode === "dark" ? logowhite : logo}
          alt={t("setup.logoAlt")}
          style={{ height: 50 }}
          loading="lazy"
        />
      </Box>
      <Paper
            elevation={6}
            sx={{
                padding: 4,
                width: "90%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: 3,
                bgcolor: theme.palette.background.paper,
            }}
            >
            <Typography variant="subtitle1" gutterBottom>
              {t("setup.subtitle")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1, minWidth: 200 }}
                id="setup-phase-hint"
              >
                {phaseHint}
              </Typography>
              {resolveProjectId() ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleReloadSnapshot}
                  disabled={snapshotReloadBusy || loading}
                  aria-label={t("setup.reloadFromServerAria")}
                >
                  {snapshotReloadBusy
                    ? t("setup.reloadFromServerBusy")
                    : t("setup.reloadFromServer")}
                </Button>
              ) : null}
            </Box>
            {finishError ? (
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {finishError}
              </Alert>
            ) : null}
            {snapshotReloadError ? (
              <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
                {snapshotReloadError}
              </Alert>
            ) : null}
            {billingHint ? (
              <Alert
                severity="warning"
                sx={{ width: "100%", mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate("/settings/billing")}>
                    Ver plano
                  </Button>
                }
              >
                {billingHint}
              </Alert>
            ) : null}

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{ mb: 4 }}
              aria-label={t("setup.stepperAria")}
            >
            {steps.map((label) => (
                <Step key={label}>
                <StepLabel>{label}</StepLabel>
                </Step>
            ))}
            </Stepper>

            {renderStepContent()}

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, width: "100%" }}>
              <Button color="inherit" onClick={handleRequestExit}>
                {t("setup.exitToApp")}
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button onClick={handleBack} disabled={activeStep === 0}>
                  {t("setup.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={activeStep >= steps.length || loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : activeStep === steps.length - 1 ? (
                    t("setup.finish")
                  ) : (
                    t("setup.next")
                  )}
                </Button>
              </Box>
            </Box>
        </Paper>
        <Box mt={4} pb={2} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t("setup.footer", { year: new Date().getFullYear() })}
            </Typography>
        </Box>

      <Dialog
        open={exitDialogOpen}
        onClose={() => setExitDialogOpen(false)}
        aria-labelledby="setup-exit-title"
      >
        <DialogTitle id="setup-exit-title">{t("setup.exitDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {resolveProjectId()
              ? t("setup.exitDialog.bodyHasProject")
              : t("setup.exitDialog.bodyNoProject")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitDialogOpen(false)}>{t("setup.exitDialog.stay")}</Button>
          <Button onClick={confirmExitToApp} color="primary">
            {t("setup.exitDialog.leave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetupPage;
